import { useState } from "react";
import { type Appointment } from "@/types";
import AppointmentList from "@/components/Appointment/AppointmentList";
import AddAppointmentModal from "@/components/Appointment/AddAppointmentModal";
import { Button } from "@/components/ui/button";
import { Calendar, Plus } from "lucide-react";

type Props = {
  appointments: Appointment[];
  patientId: string;
  patientName?: string;
  onRefresh?: () => void; // Add refresh callback
};

export default function AppointmentsTab({ appointments, patientId, patientName, onRefresh }: Props) {
  const [open, setOpen] = useState(false);

  // Create a mock patient for the modal
  const patient = patientId ? {
    id: patientId,
    name: patientName || 'Selected Patient',
    age: 0,
    gender: '',
    phone: ''
  } : undefined;

  const handleAppointmentAdded = () => {
    setOpen(false);
    // Call refresh callback to update appointments data
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <div className="space-y-6">
      {/* Professional Header */}
      <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
                      <h2 className="text-xl font-semibold text-foreground">Appointments</h2>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{appointments?.length || 0} appointment{(appointments?.length || 0) !== 1 ? 's' : ''}</span>
              {appointments?.length > 0 && (
                <>
                  <span>•</span>
                  <span>Latest: {new Date(appointments[0]?.date).toLocaleDateString()}</span>
                </>
              )}
            </div>
          </div>
        </div>
        
        <Button 
          onClick={() => setOpen(true)} 
          className="medical-button-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Appointment
        </Button>
      </div>

      {/* Appointments List */}
      {appointments?.length > 0 ? (
        <AppointmentList appointments={appointments} onRefresh={onRefresh} />
      ) : (
        <div className="text-center py-8 bg-muted/30 rounded-lg border border-border">
                      <Calendar className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-sm font-medium text-foreground mb-1">No appointments scheduled</h3>
            <p className="text-xs text-muted-foreground">Schedule the first appointment for this patient</p>
        </div>
      )}

      <AddAppointmentModal
        isOpen={open}
        onClose={() => setOpen(false)}
        patientId={patientId}
        patients={patient ? [patient] : []}
        showPatientSelection={false}
        title="Schedule Appointment"
        description={`Schedule a new appointment for ${patientName || 'this patient'}`}
        onAdded={handleAppointmentAdded}
      />
    </div>
  );
}